import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Building2, Save, Upload, Mail, Send, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const listField = (val) => Array.isArray(val) ? val.join(", ") : (val || "");

export default function CompanyProfile() {
  const { user, setUser } = useAuth();
  const [p, setP] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [digestEnabled, setDigestEnabled] = useState(!!user?.daily_digest_enabled);
  const [notifEmail, setNotifEmail] = useState(user?.notification_email || user?.email || "");
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/company/profile");
      setP(data || {});
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    setDigestEnabled(!!user?.daily_digest_enabled);
    setNotifEmail(user?.notification_email || user?.email || "");
  }, [user]);

  const setField = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...p,
        service_regions: typeof p.service_regions === "string" ? p.service_regions.split(",").map(s => s.trim()).filter(Boolean) : p.service_regions,
        services: typeof p.services === "string" ? p.services.split(",").map(s => s.trim()).filter(Boolean) : p.services,
        industries_served: typeof p.industries_served === "string" ? p.industries_served.split(",").map(s => s.trim()).filter(Boolean) : p.industries_served,
        certifications: typeof p.certifications === "string" ? p.certifications.split(",").map(s => s.trim()).filter(Boolean) : p.certifications,
        languages: typeof p.languages === "string" ? p.languages.split(",").map(s => s.trim()).filter(Boolean) : p.languages,
        years_in_business: p.years_in_business ? Number(p.years_in_business) : null,
        min_contract_size: p.min_contract_size ? Number(p.min_contract_size) : null,
        max_contract_size: p.max_contract_size ? Number(p.max_contract_size) : null,
      };
      const { data } = await api.put("/company/profile", payload);
      setP(data);
      toast.success("Profil enregistré");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const uploadPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/upload-profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setP(data.profile || {});
      toast.success("Profil extrait et fusionné", { description: data.extracted?.summary?.slice(0, 100) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'extraction");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const saveNotif = async () => {
    try {
      const { data } = await api.put("/notifications/preferences", {
        daily_digest_enabled: digestEnabled,
        notification_email: notifEmail || undefined,
      });
      setUser(data);
      toast.success("Préférences enregistrées");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const sendTestDigest = async () => {
    setTestingEmail(true);
    try {
      const { data } = await api.post("/notifications/send-test-digest");
      if (data.ok) toast.success("Courriel de test envoyé", { description: data.to });
      else toast.error("Envoi impossible (vérifier la configuration)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="company-profile-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Building2 className="h-7 w-7 text-cyan-400" /> Mon entreprise
        </h1>
        <p className="text-muted-foreground mt-1">L'IA utilise ces informations pour mieux qualifier vos opportunités.</p>
      </div>

      {/* PDF upload */}
      <Card className="p-6 bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 border-cyan-500/20">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold mb-1">Analyse IA de votre document</h3>
            <p className="text-sm text-muted-foreground mb-3">Téléversez votre capacité de service, brochure ou proposition (PDF). L'IA extrait services, certifications et coordonnées pour améliorer chaque futur lead.</p>
            <input ref={fileRef} type="file" accept=".pdf" onChange={uploadPDF} className="hidden" data-testid="pdf-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pdf-upload-button">
              {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse en cours…</> : <><Upload className="h-4 w-4 mr-2" /> Téléverser un PDF</>}
            </Button>
            {p.last_pdf_extracted_at && (
              <Badge className="ml-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 text-[10px]">
                Dernière analyse: {new Date(p.last_pdf_extracted_at).toLocaleString("fr-CA")}
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Profile form */}
      <Card className="p-6 bg-card border-border space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Nom de l'entreprise</Label><Input value={p.company_name || ""} onChange={(e) => setField("company_name", e.target.value)} data-testid="profile-company-name" className="mt-1.5" /></div>
          <div><Label>Site web</Label><Input value={p.website || ""} onChange={(e) => setField("website", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Téléphone</Label><Input value={p.phone || ""} onChange={(e) => setField("phone", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Adresse</Label><Input value={p.address || ""} onChange={(e) => setField("address", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div><Label>Description</Label><Textarea value={p.description || ""} onChange={(e) => setField("description", e.target.value)} rows={3} className="mt-1.5" /></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Régions desservies (séparées par virgule)</Label><Input value={listField(p.service_regions)} onChange={(e) => setField("service_regions", e.target.value)} className="mt-1.5" data-testid="profile-regions" /></div>
          <div><Label>Services offerts (séparés par virgule)</Label><Input value={listField(p.services)} onChange={(e) => setField("services", e.target.value)} className="mt-1.5" data-testid="profile-services" /></div>
          <div><Label>Secteurs desservis</Label><Input value={listField(p.industries_served)} onChange={(e) => setField("industries_served", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Certifications</Label><Input value={listField(p.certifications)} onChange={(e) => setField("certifications", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Taille de l'équipe</Label><Input value={p.team_size || ""} onChange={(e) => setField("team_size", e.target.value)} className="mt-1.5" placeholder="Ex: 15 employés" /></div>
          <div><Label>Années d'expérience</Label><Input type="number" value={p.years_in_business || ""} onChange={(e) => setField("years_in_business", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Assurance responsabilité</Label><Input value={p.insurance || ""} onChange={(e) => setField("insurance", e.target.value)} className="mt-1.5" placeholder="Ex: 5M$" /></div>
          <div><Label>Langues (séparées par virgule)</Label><Input value={listField(p.languages)} onChange={(e) => setField("languages", e.target.value)} className="mt-1.5" placeholder="Français, Anglais" /></div>
          <div><Label>Contrat minimum ($)</Label><Input type="number" value={p.min_contract_size || ""} onChange={(e) => setField("min_contract_size", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Contrat maximum ($)</Label><Input type="number" value={p.max_contract_size || ""} onChange={(e) => setField("max_contract_size", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div className="flex justify-end">
          <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={save} disabled={saving} data-testid="save-profile-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </Card>

      {/* Notifications */}
      <Card className="p-6 bg-card border-border">
        <div className="flex items-start gap-4 mb-5">
          <div className="h-11 w-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Mail className="h-5 w-5 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold mb-1">Alertes courriel</h3>
            <p className="text-sm text-muted-foreground">Recevez chaque matin un résumé des nouvelles opportunités haute priorité et des appels d'offres à échéance proche.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/40 border border-border">
            <div>
              <div className="font-medium text-sm">Résumé quotidien</div>
              <div className="text-xs text-muted-foreground">Envoyé chaque jour à 8h (heure de Montréal)</div>
            </div>
            <Switch checked={digestEnabled} onCheckedChange={setDigestEnabled} data-testid="digest-toggle" />
          </div>
          <div>
            <Label>Adresse courriel pour les alertes</Label>
            <Input value={notifEmail} onChange={(e) => setNotifEmail(e.target.value)} placeholder={user?.email} className="mt-1.5" data-testid="notification-email-input" />
            <p className="text-xs text-muted-foreground mt-1">Laisser vide pour utiliser votre courriel de compte.</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={sendTestDigest} disabled={testingEmail} data-testid="send-test-digest-button">
              {testingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer un test
            </Button>
            <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={saveNotif} data-testid="save-notifications-button">
              <Save className="h-4 w-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
